"use client"

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useEffect,
} from "react"

type CartItem = {
  product: any
  deliveryMethod: string
  validpl: object
  quantity: number
  priceAtPurchase: number
  userInput?: string
}

type CartContextType = {
  items: CartItem[]
  addItem: (item: CartItem) => void
  removeItem: (productId: string) => void
  clearCart: () => void
  totalPrice: number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const CartProvider = ({ children }: { children: ReactNode }) => {
  // مقدار اولیه از localStorage
  const [items, setItems] = useState<CartItem[]>([])
  const [loaded, setLoaded] = useState(false)


  // ذخیره خودکار در localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("cart-items")
      const parsed = stored ? JSON.parse(stored) : []
      setItems(parsed)
    } catch (e) {
      console.warn("🛒 Failed to load cart from localStorage:", e)
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (loaded) {
      try {
        localStorage.setItem("cart-items", JSON.stringify(items))
      } catch (e) {
        console.warn("🛒 Failed to save cart to localStorage:", e)
      }
    }
  }, [items, loaded])

  const addItem = (item: CartItem) => {
    setItems((prev) => {
      const exists = prev.find((i) => i.product._id === item.product._id)
      if (exists) {
        return prev.map((i) =>
          i.product._id === item.product._id
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        )
      }
      return [...prev, item]
    })
  }

  const removeItem = (productId: string) => {
    setItems((prev) => prev.filter((i) => i.product._id !== productId))
  }

  const clearCart = () => setItems([])


  const totalPrice = useMemo(() => {
    return items.reduce((sum, i) => sum + i.quantity * i.priceAtPurchase, 0)
  }, [items])

  if (!loaded) return null

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, clearCart, totalPrice }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error("useCart must be used within CartProvider")
  return ctx
}
